import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, User, ArrowRight, Loader2, ShieldCheck, Mail, Phone } from "lucide-react";
import { api } from "@/services/api";

/**
 * Activación inicial de contraseña para socios recién importados
 * por Casino Tampiqueño (que ya existen pero no pueden iniciar sesión).
 *
 * Flujo en dos pasos:
 *  1) Solicitud: numeroSocio + canal (correo/celular) →
 *     backend genera token con TTL 72 h. En dev el token viene en la
 *     respuesta como `devToken` para pruebas E2E.
 *  2) Confirmación: token + password nueva →
 *     backend valida y setea el password_hash.
 *
 * Nunca revelamos si el socio existe o el canal es correcto (mensaje
 * genérico). El token se pega manualmente si el proveedor de email/SMS
 * aún no está activo.
 */
export default function ActivarCuenta() {
  const navigate = useNavigate();
  const [paso, setPaso] = useState(1);
  const [numeroSocio, setNumeroSocio] = useState("");
  const [canal, setCanal] = useState("correo");
  const [destino, setDestino] = useState("");
  const [devToken, setDevToken] = useState("");

  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");

  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");

  const solicitar = async (e) => {
    e.preventDefault();
    setError("");
    if (!numeroSocio.trim()) {
      setError("Ingresa tu número de socio.");
      return;
    }
    setCargando(true);
    try {
      const { data } = await api.post("/socios/auth/activar/solicitar", {
        numeroSocio: numeroSocio.trim(),
        canal,
      });
      setDestino(data?.destinoOfuscado || "");
      if (data?.devToken) setDevToken(data.devToken);
      setPaso(2);
      toast.success("Solicitud enviada. Revisa tu " + canal + ".");
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "No se pudo enviar la solicitud. Intenta de nuevo.",
      );
    } finally {
      setCargando(false);
    }
  };

  const confirmar = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== password2) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (!token.trim()) {
      setError("Ingresa el token que recibiste.");
      return;
    }
    setCargando(true);
    try {
      await api.post("/socios/auth/activar/confirmar", {
        token: token.trim(),
        password,
      });
      toast.success("Contraseña establecida. Ya puedes iniciar sesión.");
      navigate("/login");
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "No se pudo activar. Verifica tu token e intenta de nuevo.",
      );
    } finally {
      setCargando(false);
    }
  };

  return (
    <div
      data-testid="screen-activar-cuenta"
      className="min-h-[70vh] flex items-center"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 w-full">
        <div className="hidden lg:flex flex-col gap-6 animate-fade-up">
          <span className="overline text-accent inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            Activar cuenta
          </span>
          <h1 className="font-heading text-4xl xl:text-5xl leading-[1.05] tracking-tight">
            Establece tu contraseña por primera vez.
          </h1>
          <p className="text-muted-foreground leading-relaxed max-w-md">
            Si acabas de recibir tu número de socio o si es tu primer acceso a
            CT Connect, te enviaremos un código único al canal que elijas para
            que fijes tu contraseña de forma segura.
          </p>
          <div className="divider-gold max-w-32" />
        </div>

        <div className="animate-fade-up delay-100">
          <div className="rounded-2xl border hairline bg-card p-8 md:p-10 shadow-soft">
            {paso === 1 ? (
              <form onSubmit={solicitar} className="flex flex-col gap-5">
                <h2 className="font-heading text-2xl mb-1">
                  Solicitar código de activación
                </h2>
                <p className="text-sm text-muted-foreground -mt-3 mb-2">
                  Elige el canal registrado por Casino Tampiqueño.
                </p>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="activar-numero-socio"
                    className="text-sm font-medium"
                  >
                    Número de socio
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      id="activar-numero-socio"
                      data-testid="activar-input-numero-socio"
                      value={numeroSocio}
                      onChange={(e) => setNumeroSocio(e.target.value)}
                      placeholder="Ej. 1024"
                      className="w-full pl-11 pr-4 py-3 rounded-xl bg-background border hairline focus:outline-none focus:ring-2 focus:ring-accent/50"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium">Canal</span>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      data-testid="activar-canal-correo"
                      onClick={() => setCanal("correo")}
                      className={`px-4 py-3 rounded-xl border hairline flex items-center gap-2 text-sm transition-all ${
                        canal === "correo"
                          ? "bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground border-transparent"
                          : "bg-background"
                      }`}
                    >
                      <Mail className="w-4 h-4" />
                      Correo
                    </button>
                    <button
                      type="button"
                      data-testid="activar-canal-celular"
                      onClick={() => setCanal("celular")}
                      className={`px-4 py-3 rounded-xl border hairline flex items-center gap-2 text-sm transition-all ${
                        canal === "celular"
                          ? "bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground border-transparent"
                          : "bg-background"
                      }`}
                    >
                      <Phone className="w-4 h-4" />
                      Celular
                    </button>
                  </div>
                </div>

                {error && (
                  <p role="alert" className="text-sm text-destructive">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="activar-btn-solicitar"
                  disabled={cargando}
                  className="mt-2 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft transition-all active:scale-[0.98] hover:opacity-90 disabled:opacity-70"
                >
                  {cargando ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Enviar código
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                <p className="text-xs text-muted-foreground text-center">
                  ¿Ya tienes contraseña?{" "}
                  <Link
                    to="/login"
                    className="text-primary dark:text-accent hover:underline underline-offset-4"
                  >
                    Iniciar sesión
                  </Link>
                </p>
              </form>
            ) : (
              <form onSubmit={confirmar} className="flex flex-col gap-5">
                <h2 className="font-heading text-2xl mb-1">
                  Establecer contraseña
                </h2>
                {destino ? (
                  <p
                    data-testid="activar-destino-ofuscado"
                    className="text-sm text-muted-foreground -mt-3 mb-2"
                  >
                    Enviamos el código a <strong>{destino}</strong>. Si no lo
                    recibes en unos minutos, verifica tu correo/celular con
                    Casino Tampiqueño.
                  </p>
                ) : (
                  <p
                    data-testid="activar-destino-sin"
                    className="text-sm text-muted-foreground -mt-3 mb-2"
                  >
                    Si tu número de socio y canal coinciden con nuestros
                    registros, recibirás el código en tu {canal}. Si no lo
                    recibes,{" "}
                    <button
                      type="button"
                      onClick={() => setPaso(1)}
                      className="text-primary dark:text-accent hover:underline underline-offset-4"
                    >
                      verifica los datos e inténtalo otra vez
                    </button>
                    .
                  </p>
                )}
                {devToken ? (
                  <div
                    data-testid="activar-dev-token"
                    className="rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-xs"
                  >
                    <strong>Modo desarrollo:</strong> tu token es{" "}
                    <code className="font-mono select-all">{devToken}</code>
                  </div>
                ) : null}

                <div className="flex flex-col gap-2">
                  <label htmlFor="activar-token" className="text-sm font-medium">
                    Código de activación
                  </label>
                  <input
                    id="activar-token"
                    data-testid="activar-input-token"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="Pega aquí el código recibido"
                    className="w-full px-4 py-3 rounded-xl bg-background border hairline font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="activar-password" className="text-sm font-medium">
                    Contraseña nueva (mín. 8 caracteres)
                  </label>
                  <div className="relative">
                    <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      id="activar-password"
                      data-testid="activar-input-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 rounded-xl bg-background border hairline focus:outline-none focus:ring-2 focus:ring-accent/50"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="activar-password-2" className="text-sm font-medium">
                    Confirmar contraseña
                  </label>
                  <div className="relative">
                    <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      id="activar-password-2"
                      data-testid="activar-input-password-2"
                      type="password"
                      value={password2}
                      onChange={(e) => setPassword2(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 rounded-xl bg-background border hairline focus:outline-none focus:ring-2 focus:ring-accent/50"
                    />
                  </div>
                </div>

                {error && (
                  <p role="alert" className="text-sm text-destructive">
                    {error}
                  </p>
                )}

                <div className="flex flex-col sm:flex-row gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setPaso(1);
                      setToken("");
                      setPassword("");
                      setPassword2("");
                      setError("");
                    }}
                    data-testid="activar-btn-volver"
                    disabled={cargando}
                    className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium border hairline transition-all hover:bg-muted disabled:opacity-70"
                  >
                    Volver
                  </button>
                  <button
                    type="submit"
                    data-testid="activar-btn-confirmar"
                    disabled={cargando}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft transition-all active:scale-[0.98] hover:opacity-90 disabled:opacity-70"
                  >
                    {cargando ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        Establecer contraseña
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
